import React from "react";
import { translate } from "@clearabee/i18n";
import {
  Panel,
  FlexGrid,
  Heading,
  Icon,
  Text,
  theme,
} from "@clearabee/ui-library-native";
import { ApiResponseData, useCatalogue } from "@clearabee/ui-sdk";
import { Image } from "components/Image/Image";
import type { instance } from "@clearabee/ui-sdk";
import { Platform, TouchableOpacity, View } from "react-native";
import placeholderImage from "lib/images/placeholder.png";

interface JobSelectionCardProps {
  job: Pick<
    ApiResponseData<typeof instance.jobs.getJob>,
    "ref" | "addressPostcode" | "items"
  >;
  onPress: () => void;
  active?: boolean;
  index?: number;
}

export const JobSelectionCard = ({
  job,
  onPress,
  active,
  index,
}: JobSelectionCardProps): React.ReactElement => {
  const { defaultCatalogue } = useCatalogue();
  return (
    <TouchableOpacity
      key={job.ref}
      onPress={onPress}
      style={{ marginBottom: theme.spacing.small }}
    >
      <Panel
        shadow={Platform.OS === "web"}
        styles={{
          padding: theme.spacing.small,
          borderColor: active
            ? theme.colors.accent.base
            : theme.colors.light.base,
          borderWidth: theme.spacing.xsmall2,
        }}
        testID={`skip-bag-collection-card${
          typeof index !== "undefined" ? "-" + index : ""
        }`}
      >
        <FlexGrid wrap="nowrap" cellSpacing="xsmall2" rowSpacing={null}>
          <FlexGrid.Cell base="100%" style={{ position: "relative" }}>
            <FlexGrid cellSpacing="xsmall" rowSpacing="xsmall">
              <FlexGrid.Cell base="50%">
                <Heading level={6} color="brand">
                  {translate("common.orderReference")}
                </Heading>
                <Heading
                  level={6}
                  styles={{
                    fontFamily: "ProximaSemiBold",
                  }}
                >
                  {job.ref}
                </Heading>
              </FlexGrid.Cell>
              <FlexGrid.Cell base="50%">
                <Heading level={6} color="brand">
                  {translate("common.postcode")}
                </Heading>
                <Heading
                  level={6}
                  styles={{
                    fontFamily: "ProximaSemiBold",
                  }}
                >
                  {job.addressPostcode}
                </Heading>
              </FlexGrid.Cell>
              <FlexGrid.Cell base="100%" style={{ paddingBottom: 0 }}>
                <Heading
                  level={6}
                  color="brand"
                  styles={{
                    marginBottom: theme.spacing.xsmall,
                  }}
                >
                  {translate("hwa.screens.selectProduct.whatsIncluded")}
                </Heading>
                <View>
                  {job.items?.map(({ title, qty, sku }) => (
                    <View
                      key={sku}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <Image
                        resizeMode="contain"
                        style={{
                          height: 40,
                          width: 40,
                          marginRight: 20,
                          marginTop: theme.spacing.xsmall2,
                        }}
                        source={
                          defaultCatalogue.items.find(
                            (item) => item.sku === sku,
                          )?.img || placeholderImage
                        }
                      />
                      <Text>{`${qty} x ${title}`}</Text>
                    </View>
                  ))}
                </View>
              </FlexGrid.Cell>
            </FlexGrid>
            {active && (
              <Icon.Tick2
                color="accent"
                style={{
                  position: "absolute",
                  right: 20,
                  bottom: 0,
                }}
              />
            )}
          </FlexGrid.Cell>
        </FlexGrid>
      </Panel>
    </TouchableOpacity>
  );
};
